/**
 * Anexar nota fiscal (decisão nº 4: a v1 ANEXA o XML emitido fora, não emite).
 *
 * O dialog valida o que dá para validar ANTES do fio: a chave de acesso de 44
 * dígitos com o dígito verificador conferido (`validarChaveDeAcesso`, o MESMO
 * validador da API) e o valor em reais legível. O que só o servidor sabe —
 * valor bate com o total do alvo, chave repetida (409) — volta como toast com
 * a mensagem dele.
 */

import { validarChaveDeAcesso } from '@locaveri/shared'
import { FileUp, LoaderCircle } from 'lucide-react'
import { type ReactElement, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { errorMessage } from '@/lib/api'
import { deCampoDeReaisParaWire, formatarMoedaDoWire } from '@/lib/dinheiro'

import { useAnexarNotaFiscal } from './api'

/** Exatamente UM alvo: fatura OU reserva — o mesmo contrato da API. */
export type AlvoDaNota =
  | { readonly invoiceId: string; readonly reservationId?: undefined }
  | { readonly reservationId: string; readonly invoiceId?: undefined }

export function DialogoDeNotaFiscal({
  alvo,
  valorEsperado,
  aoFechar,
}: {
  readonly alvo: AlvoDaNota
  /** Wire de centavos do total do alvo, só para a dica do campo. */
  readonly valorEsperado?: string | undefined
  readonly aoFechar: () => void
}): ReactElement {
  const [arquivo, setArquivo] = useState<File | null>(null)
  const [chave, setChave] = useState('')
  const [valorTexto, setValorTexto] = useState('')
  const anexar = useAnexarNotaFiscal()

  const chaveLimpa = chave.replace(/\s/g, '')
  const chaveInvalida = chaveLimpa !== '' && !validarChaveDeAcesso(chaveLimpa)
  const valorWire = deCampoDeReaisParaWire(valorTexto)
  const valorInvalido = valorTexto.trim() !== '' && valorWire === null

  const pronto = arquivo !== null && chaveLimpa !== '' && !chaveInvalida && valorWire !== null

  function enviar(): void {
    if (arquivo === null || valorWire === null) {
      return
    }
    anexar.mutate(
      {
        arquivo,
        chaveDeAcesso: chaveLimpa,
        valor: valorWire,
        ...(alvo.invoiceId === undefined ? {} : { invoiceId: alvo.invoiceId }),
        ...(alvo.reservationId === undefined ? {} : { reservationId: alvo.reservationId }),
      },
      {
        onSuccess: (nota) => {
          toast.success('Nota fiscal anexada', {
            description: `Chave …${nota.chaveDeAcesso.slice(-8)} vinculada.`,
          })
          aoFechar()
        },
        onError: (erro) => {
          toast.error('Não foi possível anexar a nota', { description: errorMessage(erro) })
        },
      },
    )
  }

  return (
    <Dialog
      open
      onOpenChange={(abrir) => {
        if (!abrir) {
          aoFechar()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Anexar nota fiscal</DialogTitle>
          <DialogDescription>
            O XML já foi emitido em outro sistema — aqui ele só é vinculado. O valor da nota precisa
            bater com o total {alvo.invoiceId === undefined ? 'da reserva' : 'da fatura'}
            {valorEsperado === undefined ? '' : ` (${formatarMoedaDoWire(valorEsperado)})`}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="nota-arquivo">Arquivo XML</Label>
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed
                border-border bg-surface px-3 py-2 text-sm text-fg-secondary hover:text-foreground"
            >
              <input
                id="nota-arquivo"
                type="file"
                className="hidden"
                accept=".xml,application/xml,text/xml"
                disabled={anexar.isPending}
                onChange={(evento) => {
                  setArquivo(evento.target.files?.[0] ?? null)
                }}
              />
              <FileUp className="size-4 shrink-0" aria-hidden="true" />
              <span className="min-w-0 truncate">
                {arquivo === null ? 'Escolher o XML da NF-e…' : arquivo.name}
              </span>
            </label>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="nota-chave">Chave de acesso</Label>
            <Input
              id="nota-chave"
              value={chave}
              inputMode="numeric"
              maxLength={54}
              className="tabular-money"
              disabled={anexar.isPending}
              onChange={(evento) => {
                setChave(evento.target.value)
              }}
              placeholder="44 dígitos"
            />
            {chaveInvalida ? (
              <p className="text-xs text-danger-fg">
                Chave inválida — confira os 44 dígitos (o dígito verificador não fecha).
              </p>
            ) : (
              <p className="text-xs text-fg-muted">
                Os 44 dígitos impressos no DANFE. Espaços são ignorados.
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="nota-valor">Valor da nota</Label>
            <Input
              id="nota-valor"
              value={valorTexto}
              inputMode="decimal"
              placeholder="0,00"
              className="tabular-money"
              disabled={anexar.isPending}
              onChange={(evento) => {
                setValorTexto(evento.target.value)
              }}
            />
            {valorInvalido ? (
              <p className="text-xs text-danger-fg">Valor inválido — use o formato 1.500,00.</p>
            ) : (
              <p className="text-xs text-fg-muted">
                Em reais. O servidor confere contra o total do alvo.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={aoFechar} disabled={anexar.isPending}>
            Cancelar
          </Button>
          <Button disabled={!pronto || anexar.isPending} onClick={enviar}>
            {anexar.isPending ? <LoaderCircle className="animate-spin" /> : null}
            Anexar nota
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
